import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bell,
  BellPlus,
  BellRing,
  Check,
  ChevronLeft,
  Lock,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  Send,
  Stethoscope,
  Trash2,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { notificationsApi } from '@/api/endpoints/notifications';
import { permissionsApi } from '@/api/endpoints/permissions';
import { groupsApi } from '@/api/endpoints/groups';
import { usersApi } from '@/api/endpoints/users';
import { pushApi } from '@/api/endpoints/push';
import { useFleetStore } from '@/stores/fleet';
import { useEntitlements } from '@/stores/entitlements';
import { ApiError } from '@/api/client';
import { translateEvent, ALARM_LABELS, translateAlarm } from '@/lib/traccarLabels';
import { DeviceAttributeDiscovery } from './DeviceAttributeDiscovery';
import { NotificationDiagnosticsModal } from './NotificationDiagnosticsModal';
import { cn } from '@/lib/utils';
import { isSoundEnabled, setSoundEnabled, playNotificationSound } from '@/lib/sound';
import type { Notification, NotificationType } from '@/api/types';
import { mensagemDeErro } from '@/lib/apiError';

/** Rótulos amigáveis dos canais (notificators do Traccar). */
const CHANNEL_LABEL: Record<string, string> = {
  web: 'Painel web',
  firebase: 'App (push)',
  mail: 'E-mail',
  telegram: 'Telegram',
  sms: 'SMS',
};
const CHANNEL_ORDER = ['web', 'firebase', 'mail', 'telegram', 'sms'];

/** Papéis que podem gerir notificações (criar/editar/excluir). Cliente final só vê. */
const MANAGE_ROLES = new Set(['admin', 'revenda', 'operador_suporte']);

export function NotificationRulesPage() {
  const papel = useEntitlements((s) => s.me?.user.papel ?? null);
  const canManage = papel == null ? true : MANAGE_ROLES.has(papel);

  const list = useQuery({ queryKey: ['notifications'], queryFn: () => notificationsApi.list() });
  const types = useQuery({ queryKey: ['notification-types'], queryFn: () => notificationsApi.types() });
  // Canais que o servidor realmente despacha; falha silenciosa cai no conjunto padrão.
  const notificators = useQuery({
    queryKey: ['notification-notificators'],
    queryFn: () => notificationsApi.notificators(),
    retry: false,
  });
  const serverChannels = useMemo(() => {
    const list = notificators.data;
    if (!list || list.length === 0) return CHANNEL_ORDER; // fallback: mostra todos
    return CHANNEL_ORDER.filter((c) => list.some((n) => n.type === c));
  }, [notificators.data]);

  const groups = useQuery({ queryKey: ['groups'], queryFn: () => groupsApi.list(), enabled: canManage });
  const users = useQuery({ queryKey: ['users', 'all'], queryFn: () => usersApi.list(), enabled: canManage, retry: false });
  const devices = useFleetStore((s) => s.devices);

  const [editing, setEditing] = useState<Notification | 'new' | null>(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [testFeedback, setTestFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const userOpts = useMemo(
    () => (users.data ?? []).map((u) => ({ id: u.id, name: u.name || u.email || `#${u.id}` })),
    [users.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter((n) => {
      const name = String(n.attributes?.name ?? '').toLowerCase();
      return name.includes(q) || translateEvent(n.type).toLowerCase().includes(q) || n.type.toLowerCase().includes(q);
    });
  }, [list.data, search]);

  const testMutation = useMutation({
    mutationFn: () => notificationsApi.test(),
    onSuccess: () => setTestFeedback({ ok: true, msg: 'Notificação de teste enviada.' }),
    onError: (e) => {
      const msg = e instanceof ApiError ? `${e.status} ${e.statusText}` : (e as Error).message;
      setTestFeedback({ ok: false, msg });
    },
  });

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <Link
        to="/notifications"
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar para notificações
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Notificações</h1>
          <p className="text-sm text-muted-foreground">
            Regras de alerta por evento: alcance por veículo/grupo, destinatários, filtro de alarme e canais (web, app, e-mail, Telegram).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!canManage && (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> Somente leitura
            </span>
          )}
          {papel === 'admin' && (
            <Link
              to="/notifications/custom"
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-input px-4 text-sm font-medium hover:bg-accent"
              title="Criar alarme a partir de um sensor ou entrada do rastreador"
            >
              <BellPlus className="h-4 w-4" />
              Alarmes personalizados
            </Link>
          )}
          {canManage && (
            <>
              <Button variant="outline" onClick={() => setDiagOpen(true)} className="gap-1.5">
                <Stethoscope className="h-4 w-4" />
                Diagnóstico
              </Button>
              <Button variant="outline" onClick={() => testMutation.mutate()} disabled={testMutation.isPending} className="gap-1.5">
                <Send className="h-4 w-4" />
                {testMutation.isPending ? 'Enviando…' : 'Testar envio'}
              </Button>
              <Button variant="outline" onClick={() => setSendOpen(true)} className="gap-1.5">
                <MessageSquare className="h-4 w-4" />
                Enviar mensagem
              </Button>
              <Button onClick={() => setEditing('new')} className="gap-1.5">
                <Plus className="h-4 w-4" />
                Nova notificação
              </Button>
            </>
          )}
        </div>
      </div>

      {testFeedback && (
        <div
          className={cn(
            'flex items-start gap-2 rounded-md px-3 py-2 text-xs font-medium',
            testFeedback.ok ? 'bg-status-running/10 text-status-running' : 'bg-status-blocked/10 text-status-blocked',
          )}
        >
          {testFeedback.ok ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" /> : <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
          {testFeedback.msg}
        </div>
      )}

      <SoundPrefCard />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome ou tipo de evento"
          className="rounded-full bg-secondary pl-9"
        />
      </div>

      {list.isLoading ? (
        <p className="p-6 text-sm text-muted-foreground">Carregando notificações…</p>
      ) : list.error ? (
        <p className="p-6 text-sm text-destructive">Erro ao carregar notificações.</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Bell className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">{search ? 'Nenhuma notificação corresponde à busca.' : 'Nenhuma regra cadastrada.'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((n) => (
            <NotificationCard key={n.id} notification={n} onEdit={canManage ? () => setEditing(n) : undefined} />
          ))}
        </div>
      )}

      {editing && (
        <NotificationEditor
          initial={editing === 'new' ? null : editing}
          types={types.data ?? []}
          serverChannels={serverChannels}
          devices={Array.from(devices.values()).map((d) => ({ id: d.id, name: d.name }))}
          groups={(groups.data ?? []).map((g) => ({ id: g.id, name: g.name }))}
          users={userOpts}
          onClose={() => setEditing(null)}
        />
      )}
      {sendOpen && <SendMessageModal users={userOpts} onClose={() => setSendOpen(false)} />}
      {diagOpen && <NotificationDiagnosticsModal onClose={() => setDiagOpen(false)} />}
    </div>
  );
}

function SendMessageModal({ users, onClose }: { users: Opt[]; onClose: () => void }) {
  const [userIds, setUserIds] = useState<number[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => pushApi.send(userIds, title.trim(), body.trim()),
    onSuccess: (r) => setResult(`Enviado para ${r.sent}/${r.tokens} aparelho(s).${r.failed ? ` Falhas: ${r.failed}.` : ''}`),
  });
  const valid = userIds.length > 0 && title.trim().length > 0 && body.trim().length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6" onClick={onClose}>
      <div className="flex max-h-[95vh] w-full max-w-md flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">Enviar mensagem</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" aria-label="Fechar"><X className="h-4 w-4" /></button>
        </header>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <p className="rounded-md bg-secondary/60 px-3 py-2 text-[11px] text-muted-foreground">
            Chega como push no app, só para usuários com o aplicativo instalado e logado.
          </p>
          <Field label="Destinatários" required><MultiSelect options={users} selected={userIds} onChange={setUserIds} placeholder="Buscar usuário" /></Field>
          <Field label="Título" required><Input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} placeholder="Título da mensagem" /></Field>
          <Field label="Mensagem" required>
            <textarea value={body} maxLength={500} rows={4} onChange={(e) => setBody(e.target.value)} placeholder="Texto da mensagem" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </Field>
          {send.error && <p className="rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">{(send.error as Error).message}</p>}
          {result && <p className="rounded-md bg-status-running/10 px-3 py-2 text-xs font-medium text-status-running">{result}</p>}
        </div>
        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          <Button type="button" variant="outline" onClick={onClose}>Fechar</Button>
          <Button type="button" disabled={!valid || send.isPending} onClick={() => { setResult(null); send.mutate(); }} className="gap-1.5">
            <Send className="h-4 w-4" />
            {send.isPending ? 'Enviando…' : 'Enviar'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

/** Preferência "web sonoro". */
function SoundPrefCard() {
  const [on, setOn] = useState(isSoundEnabled());
  const toggle = (v: boolean) => {
    setOn(v);
    setSoundEnabled(v);
    if (v) void playNotificationSound();
  };
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-full', on ? 'bg-status-running/10 text-status-running' : 'bg-secondary text-muted-foreground')}>
            {on ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
          </span>
          <div>
            <p className="text-sm font-bold">Web sonoro</p>
            <p className="text-xs text-muted-foreground">Toca um som neste navegador quando chega um alerta (precisa da aba aberta).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {on && (
            <button type="button" onClick={() => void playNotificationSound()} className="rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-accent">
              Testar som
            </button>
          )}
          <button
            type="button"
            onClick={() => toggle(!on)}
            className={cn('rounded-full px-3 py-1.5 text-xs font-bold transition-colors', on ? 'bg-status-running text-white' : 'bg-secondary text-muted-foreground hover:bg-accent')}
          >
            {on ? 'Ligado' : 'Desligado'}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Canais de uma regra: notificators CSV (Traccar 6.x) ou legado web/mail/sms + attributes.firebase. */
function channelsOf(n: Notification): string[] {
  const csv = String(n.notificators ?? '').trim();
  if (csv) return csv.split(',').map((s) => s.trim()).filter(Boolean);
  const out: string[] = [];
  if (n.web) out.push('web');
  if (n.mail) out.push('mail');
  if (n.sms) out.push('sms');
  if (n.attributes?.firebase === true) out.push('firebase');
  return out;
}

function NotificationCard({ notification, onEdit }: { notification: Notification; onEdit?: () => void }) {
  const name = String(notification.attributes?.name ?? '').trim();
  const alarms = String(notification.attributes?.alarms ?? '').split(',').map((s) => s.trim()).filter(Boolean);
  const channels = channelsOf(notification);
  return (
    <Card>
      <CardContent className="space-y-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <BellRing className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{name || translateEvent(notification.type)}</p>
              {name && <p className="truncate text-[11px] text-muted-foreground">{translateEvent(notification.type)}</p>}
            </div>
          </div>
          {onEdit && (
            <button type="button" onClick={onEdit} className="rounded-md p-1.5 text-muted-foreground hover:bg-accent" aria-label="Editar">
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 text-[11px]">
          {channels.map((c) => (
            <span key={c} className="inline-flex items-center gap-1 rounded-full bg-secondary px-2 py-0.5 font-semibold text-muted-foreground">
              {CHANNEL_LABEL[c] ?? c}
            </span>
          ))}
          {notification.always && <span className="rounded-full bg-status-off/15 px-2 py-0.5 font-semibold text-status-off">Sempre</span>}
        </div>

        {alarms.length > 0 && (
          <p className="text-[11px] text-muted-foreground">
            <span className="font-semibold">Alarmes:</span> {alarms.slice(0, 4).map((a) => translateAlarm(a)).join(', ')}
            {alarms.length > 4 ? ` +${alarms.length - 4}` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface Opt {
  id: number;
  name: string;
}

/** Multi-seleção com busca (checkbox list). */
function MultiSelect({ options, selected, onChange, placeholder }: { options: Opt[]; selected: number[]; onChange: (ids: number[]) => void; placeholder: string }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.name.toLowerCase().includes(s)) : options;
  }, [options, q]);
  const toggle = (id: number) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="rounded-md border border-input">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="h-9 w-full rounded-t-md border-b border-input bg-background px-2 text-sm outline-none"
      />
      <div className="max-h-36 overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">Nada encontrado.</p>
        ) : (
          filtered.map((o) => (
            <label key={o.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-accent">
              <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="h-4 w-4 accent-primary" />
              <span className="truncate">{o.name}</span>
            </label>
          ))
        )}
      </div>
      {selected.length > 0 && <p className="border-t border-input px-2 py-1 text-[11px] text-muted-foreground">{selected.length} selecionado(s)</p>}
    </div>
  );
}

interface FormState {
  name: string;
  type: string;
  alarms: string[];
  channels: string[];
  allDevices: boolean;
  deviceIds: number[];
  groupIds: number[];
  userIds: number[];
  always: boolean;
}

function fromNotification(n: Notification): FormState {
  return {
    name: String(n.attributes?.name ?? ''),
    type: n.type ?? '',
    alarms: String(n.attributes?.alarms ?? '').split(',').map((s) => s.trim()).filter(Boolean),
    channels: channelsOf(n),
    allDevices: true, // o Traccar não expõe os vínculos existentes; alcance é aditivo
    deviceIds: [],
    groupIds: [],
    userIds: [],
    always: n.always ?? false,
  };
}

const ALARM_OPTIONS = Object.entries(ALARM_LABELS)
  .map(([value, label]) => ({ value, label: `${label} (${value})` }))
  .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));

function NotificationEditor({
  initial,
  types,
  serverChannels,
  devices,
  groups,
  users,
  onClose,
}: {
  initial: Notification | null;
  types: NotificationType[];
  serverChannels: string[];
  devices: Opt[];
  groups: Opt[];
  users: Opt[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(
    initial
      ? fromNotification(initial)
      : { name: '', type: types[0]?.type ?? 'alarm', alarms: [], channels: ['web'], allDevices: true, deviceIds: [], groupIds: [], userIds: [], always: false },
  );
  const [touched, setTouched] = useState(false);
  const [alarmQuery, setAlarmQuery] = useState('');
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (initial) setForm(fromNotification(initial));
  }, [initial]);

  const errors = useMemo(() => {
    const e: Partial<Record<string, string>> = {};
    if (!form.type) e.type = 'Obrigatório';
    if (form.channels.length === 0) e.channels = 'Escolha pelo menos 1 canal';
    if (!form.allDevices && form.deviceIds.length === 0 && form.groupIds.length === 0)
      e.scope = 'Selecione ≥1 veículo/grupo ou ligue "Todos os dispositivos"';
    return e;
  }, [form]);
  const valid = Object.keys(errors).length === 0;

  const alarmMatches = useMemo(() => {
    const s = alarmQuery.trim().toLowerCase();
    if (!s) return ALARM_OPTIONS.slice(0, 8);
    return ALARM_OPTIONS.filter((a) => a.label.toLowerCase().includes(s) || a.value.toLowerCase().includes(s)).slice(0, 12);
  }, [alarmQuery]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const attributes: Record<string, unknown> = { ...(initial?.attributes ?? {}) };
      attributes.name = form.name.trim() || undefined;
      attributes.firebase = form.channels.includes('firebase') || undefined;
      if (form.type === 'alarm' && form.alarms.length) attributes.alarms = form.alarms.join(',');
      else delete attributes.alarms;
      // grava só canais que o servidor despacha (canal não suportado quebra a entrega dos demais)
      const notificators = form.channels.filter((c) => serverChannels.includes(c)).join(',');
      const payload: Notification = {
        ...(initial ?? { id: 0, type: '' }),
        type: form.type,
        always: form.always,
        notificators,
        attributes,
      } as Notification;

      let id: number;
      if (initial) {
        await notificationsApi.update(initial.id, payload);
        id = initial.id;
      } else {
        const { id: _omit, ...rest } = payload;
        void _omit;
        const created = await notificationsApi.create(rest);
        id = created.id;
      }
      // Vínculos (aditivo). Ignora falhas individuais (ex.: vínculo já existente = 400).
      const links: Promise<unknown>[] = [];
      if (!form.allDevices) {
        for (const deviceId of form.deviceIds) links.push(permissionsApi.link({ deviceId, notificationId: id }));
        for (const groupId of form.groupIds) links.push(permissionsApi.link({ groupId, notificationId: id }));
      }
      for (const userId of form.userIds) links.push(permissionsApi.link({ userId, notificationId: id }));
      await Promise.allSettled(links);
      return id;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => notificationsApi.delete(initial!.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] });
      onClose();
    },
  });

  const errorText = mensagemDeErro;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const sortedTypes = useMemo(
    () => [...types].sort((a, b) => translateEvent(a.type).localeCompare(translateEvent(b.type), 'pt-BR')),
    [types],
  );

  const addAlarm = (code: string) => {
    const c = code.trim();
    if (c && !form.alarms.includes(c)) set('alarms', [...form.alarms, c]);
    setAlarmQuery('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-2 sm:p-6" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        className="flex max-h-[95vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-card shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-base font-bold">{initial ? 'Editar notificação' : 'Nova notificação'}</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-accent" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <Field label="Nome da regra">
            <Input value={form.name} maxLength={80} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: SOS da frota, Entrada de porão" />
          </Field>

          <Field label="Tipo de evento" required error={touched ? errors.type : undefined}>
            <select value={form.type} onChange={(e) => set('type', e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
              {sortedTypes.map((t) => (
                <option key={t.type} value={t.type}>{translateEvent(t.type)}</option>
              ))}
            </select>
          </Field>

          {form.type === 'alarm' && (
            <>
            <Field label="Subtipos de alarme (deixe vazio = qualquer alarme)">
              <div className="rounded-md border border-input p-2">
                {form.alarms.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {form.alarms.map((a) => (
                      <span key={a} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                        {translateAlarm(a)}
                        <button type="button" onClick={() => set('alarms', form.alarms.filter((x) => x !== a))} aria-label="remover">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  value={alarmQuery}
                  onChange={(e) => setAlarmQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlarm(alarmQuery); } }}
                  placeholder="Buscar alarme ou digitar código e Enter"
                  className="h-9 w-full rounded border border-input bg-background px-2 text-sm outline-none"
                />
                {alarmQuery && (
                  <div className="mt-1 max-h-32 overflow-y-auto">
                    {alarmMatches.map((a) => (
                      <button key={a.value} type="button" onClick={() => addAlarm(a.value)} className="block w-full rounded px-2 py-1 text-left text-xs hover:bg-accent">
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>
            <DeviceAttributeDiscovery devices={devices} onAddAlarms={(codes) => set('alarms', Array.from(new Set([...form.alarms, ...codes])))} />
            </>
          )}

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground">Canais {touched && errors.channels && <span className="text-status-blocked">· {errors.channels}</span>}</p>
            {serverChannels.map((c) => (
              <Toggle
                key={c}
                label={CHANNEL_LABEL[c] ?? c}
                checked={form.channels.includes(c)}
                onChange={(v) => set('channels', v ? [...form.channels, c] : form.channels.filter((x) => x !== c))}
              />
            ))}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <Toggle label="Todos os dispositivos" checked={form.allDevices} onChange={(v) => set('allDevices', v)} />
            {!form.allDevices && (
              <div className="space-y-2">
                <p className="text-[11px] text-muted-foreground">Veículos</p>
                <MultiSelect options={devices} selected={form.deviceIds} onChange={(ids) => set('deviceIds', ids)} placeholder="Buscar veículo" />
                <p className="text-[11px] text-muted-foreground">Grupos</p>
                <MultiSelect options={groups} selected={form.groupIds} onChange={(ids) => set('groupIds', ids)} placeholder="Buscar grupo" />
                {touched && errors.scope && <p className="text-[11px] font-semibold text-status-blocked">{errors.scope}</p>}
                {initial && <p className="text-[10px] text-muted-foreground">O vínculo é aditivo; para remover um alcance, exclua e recrie a regra.</p>}
              </div>
            )}
          </div>

          <div className="space-y-2 rounded-md border border-border p-3">
            <p className="text-xs font-semibold text-muted-foreground">Destinatários (usuários)</p>
            <MultiSelect options={users} selected={form.userIds} onChange={(ids) => set('userIds', ids)} placeholder="Buscar usuário" />
            <p className="text-[10px] text-muted-foreground">Além de você (criador). Vazio = só o criador. Aditivo.</p>
          </div>

          <Toggle label="Notificar sempre (mesmo fora dos horários do calendário)" checked={form.always} onChange={(v) => set('always', v)} />

          {(saveMutation.error || deleteMutation.error) && (
            <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {errorText(saveMutation.error ?? deleteMutation.error)}
            </p>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
          {initial && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => { if (window.confirm('Excluir esta regra de notificação?')) deleteMutation.mutate(); }}
              disabled={deleteMutation.isPending || saveMutation.isPending}
              className="mr-auto gap-1.5"
            >
              <Trash2 className="h-4 w-4" />
              {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
            </Button>
          )}
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
            <Check className="h-4 w-4" />
            {saveMutation.isPending ? 'Salvando…' : initial ? 'Salvar' : 'Criar'}
          </Button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, required, error, children }: { label: string; required?: boolean; error?: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 cursor-pointer rounded border-border accent-primary" />
      <span className="font-medium">{label}</span>
    </label>
  );
}
